<template>
  <div class="roc-task-container">
    <div class="header-section">
      <h2 class="page-title">ROC 任务调度记录</h2>
      <p class="page-subtitle">管理机器人与任务的绑定关系及执行状态 (DeRAS 调度中心)</p>
    </div>

    <div class="search-bar">
      <el-input
        v-model="searchKeyword"
        placeholder="请输入任务名称或机器人名称搜索"
        style="width: 350px"
        clearable
        prefix-icon="el-icon-search"
      ></el-input>
      <el-button type="primary" icon="el-icon-refresh" @click="init">刷新数据</el-button>
    </div>

    <div class="card-list" v-loading="loading">
      <transition-group name="list-animate">
        <el-card 
          v-for="item in filteredTableData" 
          :key="item.id" 
          class="roc-card" 
          :class="{ 'is-completed': item.status === 2, 'is-terminated': item.status === 1 }"
          shadow="hover"
        >
          <div class="card-header">
            <div class="task-info">
              <i class="el-icon-s-order"></i>
              <span class="task-name">{{ item.taskName || '未命名任务' }}</span>
              <el-tag size="mini" :type="getStatusTagType(item.status)" effect="dark" style="margin-left: 10px;">
                {{ getStatusText(item.status) }}
              </el-tag>
            </div>

            <div class="header-actions" v-if="item.status === 0">
              <el-button type="text" style="color: #E6A23C" icon="el-icon-wallet" @click="handleSettle(item)">结算</el-button>
              <el-button type="text" style="color: #F56C6C; margin-left: 10px;" icon="el-icon-circle-close" @click="handleTerminate(item)">终止</el-button>
            </div>
            <div v-else class="header-actions disabled-text">
              <span><i :class="item.status === 2 ? 'el-icon-success' : 'el-icon-info'"></i> {{ item.status === 2 ? '已归档' : '已停止' }}</span>
            </div>
          </div>

          <el-divider></el-divider>

          <div class="card-body">
            <div class="info-row">
              <div class="info-item"><span class="label">执行机器人：</span><span class="value highlight">{{ item.rocName }}</span></div>
              <div class="info-item"><span class="label">机器人 ID：</span><span class="value">{{ item.rocId }}</span></div>
            </div>
            <div class="info-row">
              <div class="info-item"><span class="label">任务 ID：</span><span class="value">{{ item.taskId }}</span></div>
              <div class="info-item"><span class="label">调度时间：</span><span class="value">{{ formatDate(item.createTime) }}</span></div>
            </div>
            
            <div class="progress-section">
              <div class="progress-info"><span>实时进度：</span><span class="percent-text">{{ getProgressValue(item) }}%</span></div>
              <el-progress :percentage="getProgressValue(item)" :stroke-width="8" :status="getProgressStatus(item.status)" :color="getProgressColor(item.status)"></el-progress>
            </div>
          </div>

          <div class="card-footer">
            <span class="chain-address"><i class="el-icon-link"></i> 绑定流水: {{ item.id }} | 指令已上链</span>
          </div>
        </el-card>
      </transition-group>
    </div>

    <el-dialog title="任务结算与地理校验" v-model="settleDialogVisible" width="460px" destroy-on-close>
      <el-form :model="settleForm" :rules="rules" ref="settleForm" label-width="120px" size="small">
        <el-form-item label="任务编号"><el-input v-model="settleForm.task_id" disabled></el-input></el-form-item>
        
        <el-divider content-position="left">机器人位置上报</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="当前经度" prop="current_lng" label-width="80px">
              <el-input-number v-model="settleForm.current_lng" :precision="6" :step="0.0001" style="width:100%"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="当前纬度" prop="current_lat" label-width="80px">
              <el-input-number v-model="settleForm.current_lat" :precision="6" :step="0.0001" style="width:100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">结算参数设置</el-divider>
        <el-form-item label="累计里程 (L)"><el-input-number v-model="settleForm.L" :min="1" style="width:100%"></el-input-number></el-form-item>
        <el-form-item label="累计时长 (T)"><el-input-number v-model="settleForm.T" :min="1" style="width:100%"></el-input-number></el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="settleDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitSettle" :loading="settleLoading">提交位置校验并结算</el-button>
      </div>
    </el-dialog>

    <div class="pagination-wrapper" v-if="total > 0">
      <el-pagination background :page-size="pageSize" layout="total, prev, pager, next" :total="total" v-model:current-page="currentPage"></el-pagination>
    </div>
  </div>
</template>

<script>
import axios from "axios";
const API_BASE = 'http://localhost:8090/user/';

export default {
  data() {
    return {
      tableData: [],
      loading: false,
      searchKeyword: '',
      currentPage: 1,
      pageSize: 6,
      total: 0,
      activeTaskName: '',
      calcResult: { ready: false, L: 0, T: 0, offset: 0 },
      simulateProgress: {},
      settleDialogVisible: false,
      settleLoading: false,
      settleForm: { id: '', task_id: '', L: 1000, T: 900, current_lng: 116.397128, current_lat: 39.916527 },
      rules: {
        current_lng: [{ required: true, message: '请录入经度', trigger: 'blur' }],
        current_lat: [{ required: true, message: '请录入纬度', trigger: 'blur' }]
      }
    };
  },
  computed: {
    filteredTableData() {
      let data = this.tableData;
      if (this.searchKeyword) {
        const key = this.searchKeyword.toLowerCase();
        data = data.filter(i => (i.taskName?.toLowerCase().includes(key) || i.rocName?.toLowerCase().includes(key)));
      }
      return data.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize);
    }
  },
  mounted() { this.init(); },
  methods: {
    async init() {
      this.loading = true;
      try {
        const res = await axios.get(API_BASE + 'getAllTaskRoc');
        if (res.data?.code === 200) {
          this.tableData = res.data.data || [];
          this.total = this.tableData.length;
          this.tableData.forEach(item => {
            if (item.status === 0 && !this.simulateProgress[item.id]) {
              this.$set(this.simulateProgress, item.id, Math.floor(Math.random() * 40) + 20);
            }
          });
        }
      } finally { this.loading = false; }
    },
    getStatusText(s) { return { 0: '执行中', 1: '已终止', 2: '已完成' }[s] || '未知'; },
    getStatusTagType(s) { return { 0: 'primary', 1: 'info', 2: 'success' }[s] || 'info'; },
    getProgressValue(item) { return item.status === 2 ? 100 : (item.status === 1 ? 0 : this.simulateProgress[item.id] || 0); },
    getProgressColor(s) { return s === 1 ? '#909399' : (s === 2 ? '#67C23A' : '#409EFF'); },
    getProgressStatus(s) { return s === 2 ? 'success' : (s === 1 ? 'exception' : null); },
    formatDate(d) { return d ? new Date(d).toLocaleString() : '--'; },

  handleSettle(row) {
    this.activeTaskName = row.taskName;
    this.settleForm = { 
      id: row.id, 
      task_id: row.taskId, 
      current_lng: 121.4737, // 默认给个坐标示例
      current_lat: 31.2304 
    };
    this.calcResult.ready = false;
    this.settleDialogVisible = true;
    this.autoCalculate(row); // 弹窗时先算一次时长
  },

autoCalculate(row) {
    if(!this.settleForm.current_lng || !this.settleForm.current_lat) return;
    
    const startTime = new Date(row.createTime).getTime();
    const now = new Date().getTime();
    
    this.calcResult.T = Math.floor((now - startTime) / 1000);
    this.calcResult.L = "正在计算..."; // 里程需要后端地址库，前端仅占位或展示
    this.calcResult.ready = true;
    
    // 如果需要前端实时算距离，可以引入上述的 Haversine 函数
    // 这里为了演示，我们假设 L 已通过初始化数据加载或由后端接口返回
  },

    async submitSettle() {
    this.$refs.settleForm.validate(async (valid) => {
      if (!valid) return;
      this.settleLoading = true;
      try {
        const res = await axios.post(API_BASE + 'settleTask', this.settleForm);
        if (res.data.code === 200) {
          this.$notify({ 
            title: '结算成功', 
            message: '包含地理校验、时间校验及奖励自动入账', 
            type: 'success' 
          });
          this.settleDialogVisible = false;
          this.init(); // 刷新页面，此时机器人余额已更新
        } else {
          this.$message.error(res.data.msg);
        }
      } catch(e) {
        this.$message.error("系统连接失败");
      } finally { this.settleLoading = false; }
    });
  },

    handleTerminate(row) {
      this.$confirm('确定终止该调度任务吗？', '警告', { type: 'warning' }).then(async () => {
        const res = await axios.delete(API_BASE + 'deleteTaskRoc', { params: { id: row.id } });
        if (res.data.code === 200) { this.$message.success("已终止"); this.init(); }
      });
    }
  }
};
</script>

<style lang="less" scoped>
/* 保持样式简洁，重点优化卡片状态显示 */
.roc-task-container { padding: 30px; background-color: #f8fafc; min-height: 100vh; }
.header-section { text-align: center; margin-bottom: 30px; }
.search-bar { display: flex; justify-content: center; gap: 15px; margin-bottom: 30px; }
.card-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 25px; }
.roc-card { border-radius: 12px; transition: all 0.3s ease; 
  &.is-completed { background-color: #f0f9eb; opacity: 0.9; }
  &.is-terminated { background-color: #f4f4f5; opacity: 0.7; }
  &:hover { transform: translateY(-5px); box-shadow: 0 8px 16px rgba(0,0,0,0.1); }
  display: flex;
}
.card-header { display: flex; justify-content: space-between; align-items: center; font-weight: bold; }
.progress-section { margin-top: 15px; .progress-info { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 5px; } }
.card-footer { margin-top: 15px; border-top: 1px solid #eee; padding-top: 10px; font-size: 11px; color: #999; }
.pagination-wrapper { margin-top: 30px; display: flex; justify-content: center; }
.calc-preview {
  background: #fdf6ec;
  padding: 10px;
  border-radius: 8px;
  margin-top: 15px;
  .preview-item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 13px;
  }
}

</style>